/**
 * AppICEMAP — CategoryPills Component
 * Horizontally scrollable filter chips for report categories.
 *
 * Tag: <category-pills>
 *
 * Attributes:
 *   selected — comma-separated list of active category slugs
 *
 * Dispatches:
 *   category-pills:toggle → { slug: string, selected: boolean }
 *
 * Usage:
 *   <category-pills></category-pills>
 *   pills.categories = [...];
 *   pills.reportCounts = { ice: 3, police: 1 };
 */

import { state, subscribe } from "../../store/index.js";
import {
	getCategoryStyle,
	categoryIconHtml,
} from "../../utils/categoryStyle.js";

export class CategoryPills extends HTMLElement {
	constructor() {
		super();
		/** @type {Array} category definitions */
		this._categories = [];
		/** @type {Record<string, number>} report counts per category slug */
		this._reportCounts = {};
		/** @type {Set<string>} currently selected slugs */
		this._selected = new Set();
		/** @type {Function} unsubscribe */
		this._unsub = null;
	}

	connectedCallback() {
		this._render();
		this._bindEvents();

		// Subscribe to categories from store
		this._unsub = subscribe("categories", (cats) => {
			this._categories = Array.isArray(cats) ? cats : [];
			this._renderPills();
		});

		// Initial load from store
		if (state.categories && state.categories.length > 0) {
			this._categories = state.categories;
			this._renderPills();
		}
	}

	disconnectedCallback() {
		if (this._unsub) this._unsub();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	set categories(val) {
		this._categories = Array.isArray(val) ? val : [];
		this._renderPills();
	}

	set reportCounts(val) {
		this._reportCounts = val || {};
		this._renderPills();
	}

	set selected(val) {
		if (typeof val === "string") {
			this._selected = new Set(val ? val.split(",").filter(Boolean) : []);
		} else if (Array.isArray(val)) {
			this._selected = new Set(val);
		}
		this._updateActiveStates();
	}

	get selected() {
		return [...this._selected];
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `
      <nav class="category-pills" role="tablist" aria-label="Filtrar por categoría">
      </nav>
    `;
		this._nav = this.querySelector(".category-pills");
		this._renderPills();
	}

	_renderPills() {
		if (!this._nav) return;

		const totalCount = Object.values(this._reportCounts).reduce(
			(a, b) => a + b,
			0,
		);
		const allSelected = this._selected.size === 0;

		let html = "";

		// "Todos" pill
		html += `
      <button class="pill${allSelected ? " pill--active" : ""}"
              role="tab" aria-pressed="${allSelected}"
              data-slug="all">
        <i class="ph ph-list" aria-hidden="true"></i>
        Todos
        ${totalCount > 0 ? `<span class="pill__count">${totalCount}</span>` : ""}
      </button>
    `;

		// Category pills
		for (const cat of this._categories) {
			if (!cat || cat.is_active === false) continue;
			// A record without a slug can't be filtered on or counted, and used
			// to throw inside escapeAttr and take the whole pill bar down.
			if (!cat.slug) {
				console.warn("[CategoryPills] Categoría sin slug, omitida:", cat);
				continue;
			}
			const isActive = this._selected.has(cat.slug);
			const count = this._reportCounts[cat.slug] || 0;

			const { color } = getCategoryStyle(cat);

			html += `
        <button class="pill${isActive ? " pill--active" : ""}"
                role="tab" aria-pressed="${isActive}"
                data-slug="${escapeAttr(cat.slug)}">
          <span class="pill__icon" style="color:${color}">${categoryIconHtml(cat)}</span>
          ${escapeHtml(cat.label)}
          ${count > 0 ? `<span class="pill__count">${count}</span>` : ""}
        </button>
      `;
		}

		this._nav.innerHTML = html;
	}

	/* ── Events ────────────────────────────────────────────────────────────── */

	_bindEvents() {
		this._bindScroll();

		this.addEventListener("click", (e) => {
			// Ignore the click that ends a drag-scroll
			if (this._dragMoved) {
				this._dragMoved = false;
				return;
			}

			const pill = e.target.closest(".pill");
			if (!pill) return;

			const slug = pill.dataset.slug;

			if (slug === "all") {
				// Deselect all
				this._selected.clear();
				this._updateActiveStates();
				this.dispatchEvent(
					new CustomEvent("category-pills:toggle", {
						detail: { slug: "all", selected: false },
						bubbles: true,
					}),
				);
				return;
			}

			// Toggle individual category
			const wasSelected = this._selected.has(slug);

			if (wasSelected) {
				this._selected.delete(slug);
			} else {
				this._selected.add(slug);
			}

			this._updateActiveStates();

			this.dispatchEvent(
				new CustomEvent("category-pills:toggle", {
					detail: { slug, selected: !wasSelected },
					bubbles: true,
				}),
			);
		});
	}

	/* ── Horizontal scroll (wheel + drag) ──────────────────────────────────── */

	_bindScroll() {
		const nav = this._nav;
		if (!nav) return;

		// Vertical wheel → horizontal scroll (desktop mice have no h-wheel)
		nav.addEventListener(
			"wheel",
			(e) => {
				if (nav.scrollWidth <= nav.clientWidth) return;
				// Respect real horizontal intent (trackpads) but convert vertical
				const delta =
					Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
				if (delta === 0) return;
				nav.scrollLeft += delta;
				e.preventDefault();
			},
			{ passive: false },
		);

		// Pointer drag → scroll (mouse click-and-drag); touch keeps native scroll
		let startX = 0;
		let startScroll = 0;
		let dragging = false;

		nav.addEventListener("pointerdown", (e) => {
			if (e.pointerType === "touch") return; // native touch scroll is fine
			dragging = true;
			this._dragMoved = false;
			startX = e.clientX;
			startScroll = nav.scrollLeft;
		});

		nav.addEventListener("pointermove", (e) => {
			if (!dragging) return;
			const dx = e.clientX - startX;
			if (Math.abs(dx) > 4) {
				this._dragMoved = true;
				nav.style.cursor = "grabbing";
			}
			nav.scrollLeft = startScroll - dx;
		});

		const endDrag = () => {
			dragging = false;
			nav.style.cursor = "";
		};
		nav.addEventListener("pointerup", endDrag);
		nav.addEventListener("pointercancel", endDrag);
		nav.addEventListener("pointerleave", endDrag);
	}

	_updateActiveStates() {
		const allSelected = this._selected.size === 0;
		const pills = this._nav?.querySelectorAll(".pill");
		if (!pills) return;

		for (const pill of pills) {
			const slug = pill.dataset.slug;
			if (slug === "all") {
				pill.classList.toggle("pill--active", allSelected);
				pill.setAttribute("aria-pressed", String(allSelected));
			} else {
				const isActive = this._selected.has(slug);
				pill.classList.toggle("pill--active", isActive);
				pill.setAttribute("aria-pressed", String(isActive));
			}
		}
	}
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str ?? "";
	return div.innerHTML;
}

function escapeAttr(str) {
	return String(str ?? "")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

customElements.define("category-pills", CategoryPills);
